import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Stack } from "@mui/material";
import { CommonDialog } from "../../../../components/common/CommonDialog";
import { selectMenuAboveMobileBottomSheetProps } from "../../../../components/common/MobileBottomSheet";
import {
  FormButtonGroup,
  FormDatePicker,
  FormSelectInput,
  FormTextInput,
} from "../../../../components/form";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { PatchPatient } from "../../../../services/fhir/patient/PatchPatient";
import { Dayjs } from "dayjs";

interface IEditDependentModalProps {
  open: boolean;
  onClose: () => void;
  patient: any;
}

interface IFormInput {
  name: string;
  birthDate: Dayjs | null;
  gender: string;
}

const genderOptions = [
  { value: "male", display: "Masculino" },
  { value: "female", display: "Feminino" },
];

export function EditDependentModal({
  open,
  onClose,
  patient,
}: IEditDependentModalProps) {
  const { setRefetch } = useSessionContext();

  const { control, handleSubmit, reset } = useForm<IFormInput>({
    defaultValues: {
      name: (
        patient?.name?.find((n: any) => n.use === "official")?.text ||
        patient?.name?.[0]?.text ||
        ""
      ).toUpperCase(),
      birthDate: patient?.birthDate ? dayjs(patient.birthDate) : null,
      gender: patient?.gender || "",
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: IFormInput) => {
    try {
      if (data.name !== patient?.name?.[0]?.text) {
        const officialNameIndex =
          patient?.name?.findIndex((n: any) => n.use === "official") ?? 0;

        await PatchPatient({
          patientId: patient.id,
          operation: "replace",
          path: `/name/${officialNameIndex}/text`,
          content: data.name.toUpperCase(),
        });
      }

      if (data.birthDate?.format("YYYY-MM-DD") !== patient?.birthDate) {
        await PatchPatient({
          patientId: patient.id,
          operation: "replace",
          path: "/birthDate",
          content: data.birthDate?.format("YYYY-MM-DD"),
        });
      }

      if (data.gender !== patient?.gender) {
        await PatchPatient({
          patientId: patient.id,
          operation: "replace",
          path: "/gender",
          content: data.gender,
        });
      }

      toast.success("Dados atualizados com sucesso!");
      setRefetch((prev: boolean) => !prev);
      handleClose();
    } catch {
      toast.error("Erro ao atualizar os dados. Tente novamente.");
    }
  };

  const formFooter = (
    <FormButtonGroup
      onGoBackButtonClick={handleClose}
      onNextButtonClick={() => handleSubmit(onSubmit)()}
      nextButtonText="Salvar"
      goBackButtonText="Cancelar"
    />
  );

  return (
    <CommonDialog
      isOpen={open}
      handleClose={handleClose}
      sheetTitle="Editar dependente"
      sheetSubtitle="Atualize nome, data de nascimento e sexo"
      sheetSize="large"
      footer={formFooter}
    >
      <Stack spacing={2.5} sx={{ px: 2, pt: 1.5, pb: 2 }}>
        <FormTextInput control={control} name="name" label="Nome" required />
        <FormDatePicker
          control={control}
          name="birthDate"
          label="Data de Nascimento"
          required
          maxDate={dayjs(new Date())}
        />
        <FormSelectInput
          control={control}
          name="gender"
          label="Sexo de nascimento"
          required
          selectOptions={genderOptions}
          MenuProps={selectMenuAboveMobileBottomSheetProps}
        />
      </Stack>
    </CommonDialog>
  );
}
